'use client';

/**
 * ProfessionalPricingSection — the Lawyers-tab pricing hero on /pricing.
 *
 * Renders two paid offerings for a lawyer, in two visually distinct groups:
 *   1. Starter / Pro / Max — the individual AI-research plans (demand side;
 *      ``plan_type='individual'``), which bundle a Profil+ listing free.
 *      Rendered via the shared ``PricingCardV2`` in a 3-column comparison
 *      grid, so they stay byte-for-byte in sync with the Individuals tab
 *      (same price, same checkout).
 *   2. Profil+ — the standalone directory listing (supply side of the
 *      marketplace; ``plan_type='professional'``, professional checkout).
 *      Rendered as a single compact row BELOW the grid, not as a 4th grid
 *      column: it isn't a comparable subscription tier — its own footnote
 *      says it's already included with Starter or Pro — and once a 3rd AI
 *      tier (Max) joined Starter/Pro, a 4th grid card would either force an
 *      awkward 4-column breakpoint or orphan itself alone on a second row.
 *
 * The free "claim your profile" path is intentionally NOT a card here — it
 * lives as the ``ClaimProfileFreeCard`` text banner rendered directly below
 * this section in PricingSwitcher, so the free option doesn't compete for
 * comparison with the paid SKUs.
 *
 * Profil+ data flow matches the tier-upgrade dialog exactly:
 *   DB plans (plan_type='professional') — price, annual_price_eur,
 *     display_order
 *   tier-upgrade-config.ts — DB tag → i18n key references
 *   i18n Lawyers.tierUpgrade.* — the localized copy
 *
 * The AI grid cards render the shared ``<PricingCardV2>``; the Profil+ row
 * reuses the same price/feature computation but its own compact layout
 * (``PlanFeatureList`` in ``inline`` mode) rather than the vertical PlanCard
 * shell those grid cards build on, since a full-height card reads oddly as a
 * lone single-item row.
 *
 * Profil+ CTA routing:
 *   Anonymous                         → signup modal (lands on dashboard after)
 *   Authenticated + claimed profile   → Stripe checkout directly for the
 *                                       clicked tier
 *   Authenticated + no claimed profile → directory (find their SAK row to
 *                                        claim — can't checkout without a
 *                                        professional_id)
 */

import { Loader2 } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter as useNextRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import {
  PROFESSIONAL_TIER_UI,
  type ProfessionalTierUiConfig,
} from '@/components/professionals/tier-upgrade-config';
import { Button } from '@/components/ui/button';
import { useAuthModal } from '@/contexts/auth-modal-context';
import { useAuthUser } from '@/hooks/useAuthUser';
import { Link, useRouter } from '@/i18n/routing';
import { useAppSelector } from '@/lib/redux/hooks';
import {
  selectIsPricingInitialized,
  selectLaunchPromo,
  selectPlans,
} from '@/lib/redux/selectors/pricingSelectors';
import {
  type ProfessionalPlanRow,
  fetchProfessionalPlans,
} from '@/services/professional-plans-service';
import {
  CHECK_VARIANTS,
  PricingCardV2,
  buildPlanFromServerPlan,
} from './PricingV2Section';
import { PlanFeatureList, TrialCtaSection, formatAmount } from './_primitives';
import { resolveCatalogTierCopy } from './catalog-tier-copy';
import { pricingPageVatView, vatAdjustedDisplayPrice } from './pricing-helpers';
import { computePromoMonthlyPrice, selectPromoBase } from './promoPricing';

interface ProfessionalPricingSectionProps {
  /** Billing cadence from the parent toggle — monthly vs annual pricing. */
  isAnnual: boolean;
  /**
   * VAT-display selection from the shared VatControls (lawyers are always a
   * business, so ``isBusiness`` is implied true — only the VAT-payer flag and
   * country vary). DB ``price_cents`` is SK-gross; these drive the same
   * net/gross transform the individual cards use. Defaults reproduce the prior
   * fixed SK-gross display (non-payer SK → price unchanged) for any call site
   * that hasn't wired the controls yet.
   */
  country?: string;
  isVatPayer?: boolean;
}

export function ProfessionalPricingSection({
  isAnnual,
  country = 'SK',
  isVatPayer = false,
}: ProfessionalPricingSectionProps) {
  const t = useTranslations('Lawyers.tierUpgrade');
  const tHeading = useTranslations('PricingPage');
  const locale = useLocale();
  // next-intl's router operates on a compile-time union of AppPathnames,
  // but ``/checkout/[params]`` is a dynamic path that isn't in that union.
  // For the checkout redirect we use the plain next/navigation router,
  // keeping the typed next-intl router for everything else.
  const router = useRouter();
  const nextRouter = useNextRouter();
  const { isAuthenticated, isProfessional, professionalId } = useAuthUser();
  const { openSignupModal } = useAuthModal();
  // Individual AI plans (Starter/Pro) hydrate from Redux/SSR like the
  // Individuals tab — instant, no fetch. The standalone Profil+ tier is the
  // only async card (``fetchProfessionalPlans`` below).
  const reduxPlans = useAppSelector(selectPlans);
  const isPricingInitialized = useAppSelector(selectIsPricingInitialized);
  const launchPromo = useAppSelector(selectLaunchPromo);
  const [plans, setPlans] = useState<ProfessionalPlanRow[]>([]);
  // ``loading`` stays true until the first fetch settles. While true the
  // Profil+ row below the AI grid renders as a skeleton so it doesn't pop
  // in late — the Starter/Pro/Max cards in the grid render immediately
  // from Redux.
  const [loading, setLoading] = useState(true);
  // Locks the card whose checkout session is being minted so two rapid
  // clicks can't spawn two Stripe sessions (or send the user to the wrong
  // tier's hosted page if latency bites).
  const [checkoutPendingTag, setCheckoutPendingTag] = useState<string | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const rows = await fetchProfessionalPlans();
      if (cancelled) return;
      setPlans(rows);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Trial CTA handler — mirrors PricingV2Section: anonymous users get the
  // signup modal, authenticated users jump straight to /chat where the trial
  // requests are consumed. Lawyers on this tab benefit from the same 14-day
  // Pro trial as individual users; the trial plan is an individual plan
  // (plan_type='individual'), not a professional one.
  const handleStartTrial = () => {
    if (isAuthenticated) {
      nextRouter.push(`/${locale}/chat`);
    } else {
      openSignupModal();
    }
  };

  /**
   * Paid-tier CTA — routes to the in-app embedded checkout page. The
   * checkout page will call ``/api/stripe/professional-checkout-session``
   * which forwards to the backend with ``ui_mode="embedded"``, gets back
   * a ``client_secret``, and mounts Stripe's ``<CheckoutProvider>``.
   *
   * Requires a claimed professional profile (``professionalId``). Users
   * without one go to ``/professional-directory`` first so they can find
   * and claim their SAK row — the checkout session is scoped to a
   * professional row, so there's nothing to check out without it.
   */
  const handlePaidCtaClick = (ui: ProfessionalTierUiConfig) => {
    if (!isAuthenticated) {
      openSignupModal('/professional-directory/dashboard');
      return;
    }
    if (!isProfessional || !professionalId) {
      router.push('/advokati');
      return;
    }
    if (checkoutPendingTag) return;
    setCheckoutPendingTag(ui.tag);
    // The checkout page decodes base64 JSON (see CheckoutParams in
    // checkout/[params]/page.tsx). ``type: 'professional'`` is the
    // discriminator that routes the page into the pro-specific branch.
    const encoded = btoa(
      JSON.stringify({
        type: 'professional',
        professional_id: professionalId,
        professional_tier: ui.subscriptionTier,
        professional_billing: isAnnual ? 'annual' : 'monthly',
      })
    );
    nextRouter.push(`/${locale}/checkout/${encoded}`);
  };

  // When ``fetchProfessionalPlans`` returns empty (DB unreachable or no pro
  // plans configured), ``sorted`` is [] and no Profil+ card renders. The
  // Starter/Pro AI cards hydrate from Redux and always render — a Profil+
  // fetch failure degrades gracefully rather than hiding the whole section.

  // Sort by display_order (DB-driven) so marketing can change position
  // without a code change.
  const sorted = [...plans].sort((a, b) => a.display_order - b.display_order);

  // Starter/Pro AI plans — same source + filter as the Individuals tab (paid
  // individual tiers; the free trial, price 0, is excluded), built through the
  // shared resolver so they stay in sync with PricingV2Section. They bundle a
  // Profil+ listing free, surfaced via the ``bundleBadge`` below. Hydrate from
  // Redux/SSR, so they render immediately beside the async Profil+ card.
  const aiPlans = isPricingInitialized
    ? reduxPlans
        .filter((p) => p.plan_type === 'individual' && p.price > 0)
        .map((p) => buildPlanFromServerPlan(p, tHeading, locale))
    : [];
  // Lawyers are always a business (isBusiness: true), so the SK-gross AI prices
  // route through the same VAT view as the Profil+ card below.
  const aiVatDisplay = pricingPageVatView(country, true, isVatPayer);
  const bundleBadge = tHeading('lawyerAiProfilPlusIncluded');
  const safeT = (key: string): string => {
    try {
      return tHeading(key as Parameters<typeof tHeading>[0]);
    } catch {
      return key;
    }
  };

  // No loading-state spinner: the other pricing sections on this page (user
  // plans, packs) hydrate from Redux/SSR and never flash a loader, so a spinner
  // here would look like "something's broken". The heading + Starter/Pro cards
  // render immediately; the Profil+ card streams in a few hundred ms later when
  // ``fetchProfessionalPlans`` resolves (a skeleton holds its leading slot). The
  // section wrapper + id are stable across render states, which is also what the
  // ``scrollAnchor='professional-plans'`` handoff from the dashboard depends on.

  return (
    <section
      id='professional-plans'
      className='w-full scroll-mt-24'
      aria-labelledby='professional-pricing-heading'
    >
      <div className='mx-auto max-w-5xl text-center'>
        <h2
          id='professional-pricing-heading'
          className='text-2xl font-bold text-content-primary sm:text-3xl'
        >
          {tHeading('professionalSectionHeading')}
        </h2>
        <p className='mt-3 text-base text-content-secondary'>
          {tHeading('professionalSectionDescription')}
        </p>
      </div>

      {/* Starter / Pro / Max — the AI-research plan comparison grid. Stacked
          on mobile + tablet, three-across on desktop. The intermediate
          two-column (md) step is intentionally skipped: with exactly three
          cards it would orphan the third (Max) on a half-width second row.
          Stacking until lg keeps the comparison row clean. */}
      <div className='mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-6 lg:grid-cols-3'>
        {aiPlans.map((plan) => (
          <PricingCardV2
            key={plan.id}
            plan={plan}
            isAnnual={isAnnual}
            checkVariant={CHECK_VARIANTS[plan.id.toLowerCase()] ?? 'info'}
            vatDisplay={aiVatDisplay}
            locale={locale}
            country={country}
            isBusiness
            isVatPayer={isVatPayer}
            launchPromo={launchPromo}
            bundleBadge={bundleBadge}
            t={safeT}
          />
        ))}
      </div>

      {/*
        Skeleton for the async Profil+ row while ``fetchProfessionalPlans``
        is in-flight. The AI grid above hydrates from Redux/SSR immediately,
        so without this the row below it would pop in late with a layout
        jump. Geometry approximates the real compact row rendered below.
      */}
      {loading && plans.length === 0 && (
        <div
          aria-hidden='true'
          className='mx-auto mt-6 h-24 max-w-5xl animate-pulse rounded-xl border border-line-subtle bg-surface-subtle/60'
        />
      )}

      {/* Profil+ — the standalone directory listing (professional checkout),
          rendered as a single compact row below the AI grid rather than a
          4th grid column (see file header for why). */}
      {sorted.map((row) => {
        const ui = PROFESSIONAL_TIER_UI[row.tag];
        if (!ui) return null;

        // Catalog (DB) is the source of truth for plan name, tagline, and
        // feature bullets per CLAUDE.md ("Display names live in
        // plan_translations" / "Do NOT add plan/feature translation keys to
        // messages/{en,sk}.json — use catalog tables"). The ``PROFESSIONAL_TIER_UI``
        // i18n keys remain as a fallback for rows the catalog hasn't
        // backfilled yet (3-tier resolve: locale → en → legacy i18n key).
        // ``resolveCatalogTierCopy`` is the shared resolver (#5232) — the
        // /for-professionals page renders the same Profil+ tier through it.
        const copy = resolveCatalogTierCopy(row, locale);
        const displayName =
          copy.name ?? t(ui.nameKey as Parameters<typeof t>[0]); // dynamic key
        const displayDescription =
          copy.tagline ?? t(ui.descriptionKey as Parameters<typeof t>[0]); // dynamic key
        const displayFeatures =
          copy.features ??
          ui.featureKeys.map((k) => t(k as Parameters<typeof t>[0])); // dynamic key

        // Preserve sub-EUR precision (prof_profile is €0.90/mo). The prior
        // ``Math.round`` rendered 0.90 as 1, diverging from the actual
        // Stripe price shown on checkout. ``formatAmount`` renders 2
        // decimals when the amount has a fractional part.
        //
        // ``price_cents`` / ``annual_price_eur`` are SK-gross (incl. 23 %),
        // matching the individual-plan convention. ``vatAdjustedDisplayPrice``
        // strips SK VAT to net then re-applies the customer's rate (gross for
        // B2C / non-payer, net + DPH for a domestic VAT-payer, 0 % for
        // cross-border EU reverse-charge / non-EU). Lawyers are always a
        // business, hence ``isBusiness: true``.
        const monthlyGross = row.price_cents / 100;
        const annualTotalGross = row.annual_price_eur ?? monthlyGross * 12;
        const perMonthGross = isAnnual ? annualTotalGross / 12 : monthlyGross;
        const { displayPrice, vatSuffix } = vatAdjustedDisplayPrice(
          perMonthGross,
          { country, isBusiness: true, isVatPayer, locale }
        );
        // The VAT-adjusted MONTHLY headline (the 4,90 € basis). Used both as
        // the annual-vs-monthly strikethrough and as the launch-promo base.
        const vatAdjustedMonthly = vatAdjustedDisplayPrice(monthlyGross, {
          country,
          isBusiness: true,
          isVatPayer,
          locale,
        }).displayPrice;
        // On annual, strike the monthly headline (also VAT-adjusted so the
        // struck price and the live price share one basis).
        const annualStrikethrough = isAnnual ? vatAdjustedMonthly : undefined;
        // Annual savings percentage shown as the "−X %" pill next to the
        // strikethrough — mirrors the individual cards (PricingV2Section
        // L254-257). The percentage is a ratio of gross amounts, so it's
        // VAT-rate-invariant (VAT scales numerator and denominator alike) —
        // computing it off the SK-gross figures here matches the individual
        // cards' off-gross derivation and avoids a redundant VAT round-trip.
        // Only meaningful (and only rendered) when the annual total actually
        // undercuts 12× monthly; the prof_profile tier with no annual_price
        // falls back to monthlyGross*12, yielding 0 % → pill suppressed.
        const annualSavingsPct =
          isAnnual && monthlyGross > 0
            ? Math.round((1 - annualTotalGross / (monthlyGross * 12)) * 100)
            : 0;

        // Launch-promo display (#4744 / #4741 — Profil+). Mirrors PricingCardV2
        // (PricingV2Section L259-300): backend /internal/pc (launchPromo) is
        // the single source of truth and the discount is enforced at checkout
        // by the Stripe coupon — this is purely the marketing presentation.
        // The promo % is taken off the MONTHLY headline for BOTH cadences
        // (selectPromoBase, #5011) so the struck price, the discounted price
        // and the −% pill stay mutually consistent. Gated on the prof_profile
        // tag being in the active allowlist; this standalone Profil+ card is
        // the only place it renders (the bundled Starter/Pro PricingCardV2
        // cards beside it carry their own promo).
        const planDiscount = launchPromo.active
          ? launchPromo.plans[row.tag.toLowerCase()]
          : undefined;
        const promoSavingsPctRaw = planDiscount
          ? isAnnual
            ? planDiscount.year_pct
            : planDiscount.month_pct
          : null;
        const isPromoEligible =
          promoSavingsPctRaw != null && promoSavingsPctRaw > 0;
        const promoSavingsPct = isPromoEligible ? promoSavingsPctRaw! : 0;
        const promoBase = selectPromoBase(vatAdjustedMonthly, displayPrice);
        const promoDisplayPrice = isPromoEligible
          ? computePromoMonthlyPrice(promoBase, promoSavingsPct)
          : null;
        const effectiveDisplayPrice =
          promoDisplayPrice !== null ? promoDisplayPrice : displayPrice;
        const promoStrikethrough =
          promoDisplayPrice !== null ? promoBase : undefined;
        const isPending = checkoutPendingTag === row.tag;
        // Standalone Profil+ is also bundled free into the Starter/Pro AI
        // plans beside it — cross-reference it so the 4,90 € card doesn't
        // read as the only way to get a listing.
        const footnote =
          row.tag === 'prof_profile'
            ? tHeading('profilPlusBundledNote')
            : undefined;

        // Same field shape the vertical PlanCard's priceDisplay prop used —
        // kept as a plain object (rather than passed straight into a JSX
        // prop) so the compact row below can read individual fields without
        // pulling in PlanPriceDisplay's large 4xl price styling, which is
        // sized for a full-height card, not a slim row.
        const priceDisplay = {
          amount: effectiveDisplayPrice,
          currency: 'EUR',
          periodLabel: `/${t('perMonth')}`,
          // Promo strikethrough takes priority over the annual-vs-monthly one.
          strikethrough: isPromoEligible
            ? promoStrikethrough
            : annualStrikethrough,
          savingsPct: isPromoEligible
            ? promoSavingsPct
            : annualSavingsPct > 0
              ? annualSavingsPct
              : undefined,
          cadenceLabel: isAnnual
            ? tHeading('billedAnnually')
            : tHeading('billedMonthly'),
          vatSuffix: vatSuffix || undefined,
        };

        return (
          <div
            key={row.tag}
            className='mx-auto mt-6 max-w-5xl rounded-xl border border-line-default bg-surface-card p-5'
          >
            <div className='flex flex-col gap-4 sm:flex-row sm:items-center'>
              <div className='flex flex-col gap-1 sm:w-56 sm:flex-shrink-0'>
                <h3 className='text-base font-semibold text-content-primary'>
                  {displayName}
                </h3>
                {displayDescription && (
                  <p className='text-xs text-content-muted'>
                    {displayDescription}
                  </p>
                )}
                <div className='mt-1 flex flex-wrap items-baseline gap-1.5'>
                  <span className='text-xl font-bold text-content-primary'>
                    {formatAmount(priceDisplay.amount)}
                  </span>
                  <span className='text-xs text-content-muted'>
                    {priceDisplay.currency} {priceDisplay.periodLabel}
                  </span>
                  {typeof priceDisplay.strikethrough === 'number' && (
                    <span className='text-xs text-content-muted line-through'>
                      {formatAmount(priceDisplay.strikethrough)}
                    </span>
                  )}
                  {typeof priceDisplay.savingsPct === 'number' &&
                    priceDisplay.savingsPct > 0 && (
                      <span className='rounded-full bg-status-success-bg px-1.5 py-0.5 text-xs font-bold text-status-success-text'>
                        −{priceDisplay.savingsPct} %
                      </span>
                    )}
                </div>
                {(priceDisplay.vatSuffix || priceDisplay.cadenceLabel) && (
                  <p className='text-xs text-content-muted'>
                    {priceDisplay.vatSuffix && (
                      <>{priceDisplay.vatSuffix} &middot; </>
                    )}
                    {priceDisplay.cadenceLabel}
                  </p>
                )}
              </div>

              <PlanFeatureList
                features={displayFeatures}
                ariaLabel={`${displayName} features`}
                checkVariant='success'
                layout='inline'
                className='flex-1'
              />

              {/* Profil+ is the entry tier of this row — the AI cards in
                    the grid above carry the headline (popular) treatment,
                    so Profil+ always uses the quieter outline button. Label
                    is shared with the AI cards ("Subscribe" / "Predplatiť"). */}
              <Button
                variant='outline'
                disabled={isPending}
                onClick={() => handlePaidCtaClick(ui)}
                className='w-full min-h-[44px] flex-shrink-0 rounded-lg border-line-strong text-sm font-semibold text-content-primary hover:bg-interactive-hover sm:w-auto sm:px-6'
              >
                {isPending ? (
                  <Loader2
                    className='h-4 w-4 animate-spin'
                    aria-hidden='true'
                  />
                ) : (
                  tHeading('subscribeButton')
                )}
              </Button>
            </div>

            {footnote && (
              <p className='mt-3 text-xs text-content-muted'>{footnote}</p>
            )}
          </div>
        );
      })}

      {/* Free-trial CTA — same 14-day Pro trial shown on the Individuals tab,
          extracted into the shared TrialCtaSection primitive. The trial plan
          (plan_type='individual', price=0) is the same product; lawyers
          benefit from it just as individual users do. OrgPricingSection is
          intentionally excluded — no trial for firms. */}
      <TrialCtaSection
        plans={reduxPlans}
        locale={locale}
        onStartTrial={handleStartTrial}
      />

      {/* Ranking-transparency disclosure — required by zákon č. 108/2024
          + EU UCPD Art. 7(4a) for any pricing copy that mentions
          visibility / placement features. Issue #3688.
          Uses text-content-secondary (not -muted) to satisfy the
          regulation's "clearly visible" requirement, matching the
          FeaturedSlot disclosure footer. Don't downgrade without legal
          sign-off. */}
      <p className='mx-auto mt-6 max-w-3xl text-center text-xs text-content-secondary'>
        {tHeading.rich('rankingDisclosure', {
          link: (chunks) => (
            <Link
              href='/how-we-rank'
              className='text-brand-tertiary underline-offset-2 hover:underline'
            >
              {chunks}
            </Link>
          ),
        })}
      </p>
    </section>
  );
}
